import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../app.config';
import { catchErrorLogRet, mapCreate } from '../../common/rest.operators';

import { DraftPost } from './model/draft-post.model';
import { Draft } from './model/draft.model';
import { MainConfig } from './model/main-config.model';
import { Response } from './model/response.model';
import { SearchItem } from './model/search-item.model';
import { SearchParams } from './model/search-params.model';
import { TemplateVersionShallow } from './model/template/template-version-shallow.model';

@Injectable()
export class EvaluationService {
    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {}

    retrieveConfig(component: string): Observable<MainConfig> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.EVALUATION}/configs/${component}`,
            )
            .pipe(
                // mapRetrieve(),
                catchErrorLogRet({}),
            );
    }

    listTemplateVersions(
        templateId?: number,
    ): Observable<TemplateVersionShallow[]> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.EVALUATION}/templates/${
                    templateId ?? 'all'
                }/versions`,
            )
            .pipe(
                // mapList(),
                catchErrorLogRet([]),
            );
    }

    newDraft(templateVersionId: number): Observable<Draft> {
        const params = new HttpParams().set(
            'templateVersionId',
            String(templateVersionId),
        );
        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.EVALUATION}/drafts/new`, {
                params,
            })
            .pipe(
                // mapRetrieve(),
                catchErrorLogRet({}),
            );
    }

    lastDraft(
        templateVersionId: number,
        userId: number,
        targetUserId: number,
        timeZoneOffset: number,
    ): Observable<Draft> {
        const params = new HttpParams()
            .set('templateVersionId', String(templateVersionId))
            .set('userId', String(userId))
            .set('targetUserId', String(targetUserId))
            .set('timeZoneOffset', String(timeZoneOffset));
        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.EVALUATION}/drafts/last`, {
                params,
            })
            .pipe(
                // mapRetrieve(),
                catchErrorLogRet({}),
            );
    }

    createDraft(draft: DraftPost): Observable<string> {
        return this._http
            .post(`${this._appConfig.API_ENDPOINTS.EVALUATION}/drafts`, draft, {
                observe: 'response',
            })
            .pipe(
                mapCreate(),
                map((location) => /.*\/(.+)$/.exec(location)?.[1]),
                catchErrorLogRet(null),
            );
    }

    retrieveDraft(draftId: string, timeZoneOffset: number): Observable<Draft> {
        const params = new HttpParams().set(
            'timeZoneOffset',
            String(timeZoneOffset),
        );
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.EVALUATION}/drafts/${draftId}`,
                {
                    params,
                },
            )
            .pipe(
                // mapRetrieve(),
                catchErrorLogRet({}),
            );
    }

    updateDraft(draft: DraftPost): Observable<void> {
        return this._http
            .put(
                `${this._appConfig.API_ENDPOINTS.EVALUATION}/drafts/${draft.draftId}`,
                draft,
            )
            .pipe(
                // mapRetrieve(),
                catchErrorLogRet(null),
            );
    }

    publishDraft(draft: DraftPost): Observable<void> {
        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.EVALUATION}/drafts/publish`,
                draft,
            )
            .pipe(
                // mapRetrieve(),
                catchErrorLogRet(null),
            );
    }

    search(searchParams: SearchParams): Observable<SearchItem[]> {
        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.EVALUATION}/forms/search`,
                searchParams,
            )
            .pipe(
                // mapRetrieve(),
                catchErrorLogRet([]),
            );
    }

    retrieveResponse(
        responseId: number,
        timeZoneOffset: number,
    ): Observable<Response> {
        const params = new HttpParams().set(
            'timeZoneOffset',
            String(timeZoneOffset),
        );
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.EVALUATION}/responses/${responseId}`,
                {
                    params,
                },
            )
            .pipe(
                // mapRetrieve(),
                catchErrorLogRet({}),
            );
    }

    updateResponse(responseId: number, draft: DraftPost): Observable<void> {
        return this._http
            .put(
                `${this._appConfig.API_ENDPOINTS.EVALUATION}/responses/${responseId}`,
                draft,
            )
            .pipe(
                // mapRetrieve(),
                catchErrorLogRet(null),
            );
    }
}
